<template>
  <div class="order-management">
    <div class="page-header">
      <div>
        <h2 class="page-title">订单管理</h2>
        <p class="page-desc">查看和管理所有订单，支持核销、退款操作</p>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card" @click="filterOrderStatus = null; filterRefundStatus = null">
        <div class="stat-value">{{ stats.total || 0 }}</div>
        <div class="stat-label">总订单</div>
      </div>
      <div class="stat-card warning" @click="filterOrderStatus = 0; filterRefundStatus = null">
        <div class="stat-value">{{ stats.pending_pay || 0 }}</div>
        <div class="stat-label">待支付</div>
      </div>
      <div class="stat-card primary" @click="filterOrderStatus = 1; filterRefundStatus = null">
        <div class="stat-value">{{ stats.pending_use || 0 }}</div>
        <div class="stat-label">待使用</div>
      </div>
      <div class="stat-card success" @click="filterOrderStatus = 2; filterRefundStatus = null">
        <div class="stat-value">{{ stats.used || 0 }}</div>
        <div class="stat-label">已使用</div>
      </div>
      <div class="stat-card danger" @click="filterOrderStatus = 3; filterRefundStatus = null">
        <div class="stat-value">{{ stats.refunded || 0 }}</div>
        <div class="stat-label">已退款</div>
      </div>
      <div class="stat-card info" @click="filterRefundStatus = 1; filterOrderStatus = null">
        <div class="stat-value">{{ stats.refunding || 0 }}</div>
        <div class="stat-label">退款中</div>
      </div>
    </div>

    <!-- 搜索筛选栏 -->
    <div class="filter-bar">
      <el-input v-model="searchKey" placeholder="搜索订单号/用户/商品" prefix-icon="Search" clearable style="width: 200px"
        @keyup.enter="handleSearch" />
      <el-select v-model="filterOrderStatus" placeholder="订单状态" clearable style="width: 100px" @change="handleSearch">
        <el-option label="待支付" :value="0" />
        <el-option label="待使用" :value="1" />
        <el-option label="已使用" :value="2" />
        <el-option label="已退款" :value="3" />
        <el-option label="已取消" :value="4" />
      </el-select>
      <el-select v-model="filterRefundStatus" placeholder="退款状态" clearable style="width: 100px" @change="handleSearch">
        <el-option label="退款中" :value="1" />
        <el-option label="退款成功" :value="2" />
        <el-option label="退款失败" :value="3" />
      </el-select>
      <el-select v-model="storeFilter" placeholder="全部门店" clearable filterable style="width: 150px"
        @change="handleSearch">
        <el-option v-for="store in storeList" :key="store.id" :label="store.store_name" :value="store.id" />
      </el-select>
      <el-select v-model="branchFilter" placeholder="全部分店" clearable filterable style="width: 150px"
        @change="handleSearch">
        <el-option v-for="branch in branchList" :key="branch.id" :label="branch.branch_name" :value="branch.id" />
      </el-select>
      <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
        end-placeholder="结束日期" value-format="YYYY-MM-DD" style="width: 240px" @change="handleSearch" />
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 订单列表 -->
    <el-table :data="orderList" border v-loading="loading" style="width: 100%">
      <el-table-column label="订单信息" min-width="260">
        <template #default="{ row }">
          <div class="order-info">
            <div class="order-no">订单号：{{ row.order_no }}</div>
            <div class="product-name">{{ row.product_name }}</div>
            <div class="user-info">用户：{{ row.user_nickname || row.user_phone || '匿名用户' }}</div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="金额" width="130" align="center">
        <template #default="{ row }">
          <div class="amount-info">
            <div class="pay-amount">¥{{ row.pay_amount }}</div>
            <div class="quantity">x{{ row.quantity }}</div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="核销码" width="120" align="center">
        <template #default="{ row }">
          <div class="verification-code">
            <span class="code">{{ row.verification_code || '--' }}</span>
            <el-button v-if="row.verification_code" link size="small" @click="copyCode(row.verification_code)">
              <el-icon>
                <CopyDocument />
              </el-icon>
            </el-button>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="订单状态" width="90" align="center">
        <template #default="{ row }">
          <el-tag :type="getOrderStatusType(row.order_status)" size="small">
            {{ getOrderStatusText(row.order_status) }}
          </el-tag>
        </template>
      </el-table-column>

      <!-- 修复后（稳定） -->
      <el-table-column label="退款状态" width="80" align="center" v-show="filterRefundStatus !== null">
        <template #default="{ row }">
          <el-tag :type="getRefundStatusType(row.refund_status)" size="small">
            {{ getRefundStatusText(row.refund_status) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="下单时间" width="150" align="center">
        <template #default="{ row }">
          {{ formatTime(row.create_time) }}
        </template>
      </el-table-column>

      <el-table-column label="操作" width="280" fixed="right" align="center">
        <template #default="{ row }">
          <div class="action-buttons">
            <el-button v-if="row.order_status === 1 && row.pay_status === 1" type="success" size="small"
              @click="handleVerification(row)">
              核销
            </el-button>
            <el-button v-if="row.order_status === 0 && row.pay_status === 0" type="warning" size="small"
              @click="handleCancel(row)">
              取消订单
            </el-button>
            <el-button
              v-if="row.pay_status === 1 && row.refund_status === 0 && row.order_status !== 2 && row.order_status !== 3 && row.order_status !== 4"
              type="danger" size="small" plain @click="applyRefund(row)">
              申请退款
            </el-button>
            <el-button v-if="row.refund_status === 1" type="primary" size="small" plain @click="auditRefund(row)">
              审核退款
            </el-button>
            <el-button type="primary" link size="small" @click="viewDetail(row)">
              详情
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="fetchData" @current-change="fetchData" />
    </div>

    <!-- 核销弹窗 -->
    <el-dialog v-model="verificationDialogVisible" title="订单核销" width="500px">
      <div class="verification-content">
        <div class="order-info">
          <div>订单号：{{ currentOrder?.order_no }}</div>
          <div>商品：{{ currentOrder?.product_name }}</div>
          <div>核销码：<strong class="code">{{ currentOrder?.verification_code }}</strong></div>
        </div>
        <el-form :model="verificationForm" label-width="80px">
          <el-form-item label="核销员" required>
            <el-select v-model="verificationForm.staff_id" placeholder="请选择核销员" filterable style="width: 100%"
              @change="onStaffChange">
              <el-option v-for="staff in staffList" :key="staff.id" :label="`${staff.name} (${staff.phone})`"
                :value="staff.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="核销门店" v-if="verificationForm.store_name">
            <el-input :value="verificationForm.store_name" disabled />
          </el-form-item>
          <el-form-item label="核销分店" v-if="verificationForm.branch_name">
            <el-input :value="verificationForm.branch_name" disabled />
          </el-form-item>
          <el-form-item label="备注">
            <el-input v-model="verificationForm.remark" type="textarea" placeholder="请输入备注" />
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <el-button @click="verificationDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmVerification" :loading="verificationLoading">确认核销</el-button>
      </template>
    </el-dialog>

    <!-- 申请退款弹窗 -->
    <el-dialog v-model="refundApplyDialogVisible" title="申请退款" width="550px">
      <el-form :model="refundApplyForm" label-width="80px">
        <el-form-item label="退款原因" required>
          <el-input v-model="refundApplyForm.refund_reason" type="textarea" :rows="3" placeholder="请填写退款原因" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="refundApplyForm.refund_remark" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
        <el-form-item label="凭证图片">
          <div class="image-uploader">
            <el-upload :file-list="refundApplyImages" action="#" :limit="5" accept="image/*" :auto-upload="false"
              :on-change="handleRefundImageChange" :on-remove="handleRefundImageRemove" list-type="picture-card"
              multiple>
              <el-icon>
                <Plus />
              </el-icon>
              <template #tip>
                <div class="upload-tip">支持上传最多5张图片，点击或拖拽上传</div>
              </template>
            </el-upload>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="refundApplyDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRefundApply" :loading="refundApplyLoading">提交申请</el-button>
      </template>
    </el-dialog>

    <!-- 审核退款弹窗 -->
    <el-dialog v-model="refundAuditDialogVisible" title="审核退款" width="600px">
      <el-form :model="refundAuditForm" label-width="100px">
        <el-form-item label="退款金额">
          <span class="refund-amount">¥{{ currentOrder?.pay_amount }}</span>
        </el-form-item>
        <el-form-item label="退款原因">
          <span class="refund-reason">{{ currentOrder?.refund_reason }}</span>
        </el-form-item>
        <el-form-item label="用户备注" v-if="currentOrder?.refund_remark">
          <span class="refund-remark">{{ currentOrder?.refund_remark }}</span>
        </el-form-item>

        <!-- 用户凭证图片 - 添加在这里 -->
        <el-form-item label="用户凭证" v-if="currentOrder?.refund_images && currentOrder.refund_images.length">
          <div class="refund-images">
            <el-image v-for="(img, idx) in currentOrder.refund_images" :key="idx" :src="img"
              :preview-src-list="currentOrder.refund_images" fit="cover" class="refund-img" />
          </div>
        </el-form-item>

        <el-form-item label="审核结果" required>
          <el-radio-group v-model="refundAuditForm.audit_result">
            <el-radio value="approved">同意退款</el-radio>
            <el-radio value="rejected">拒绝退款</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="退款方式" v-if="refundAuditForm.audit_result === 'approved'">
          <el-radio-group v-model="refundAuditForm.refund_type">
            <el-radio value="online">线上退款（原路返回）</el-radio>
            <el-radio value="offline">线下退款</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="审核备注">
          <el-input v-model="refundAuditForm.refund_remark" type="textarea" :rows="3" placeholder="请输入审核备注" />
        </el-form-item>
        <el-form-item label="审核凭证">
          <div class="image-uploader">
            <el-upload :file-list="refundAuditImages" action="#" :limit="5" accept="image/*" :auto-upload="false"
              :on-change="handleAuditImageChange" :on-remove="handleAuditImageRemove" list-type="picture-card" multiple>
              <el-icon>
                <Plus />
              </el-icon>
              <template #tip>
                <div class="upload-tip">支持上传最多5张图片，点击或拖拽上传</div>
              </template>
            </el-upload>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="refundAuditDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRefundAudit" :loading="refundAuditLoading">确认审核</el-button>
      </template>
    </el-dialog>
    <!-- 订单详情弹窗 - 添加退款详情部分 -->
    <el-dialog v-model="detailDialogVisible" title="订单详情" width="750px">
      <div v-if="currentOrder" class="order-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="订单号">{{ currentOrder.order_no }}</el-descriptions-item>
          <el-descriptions-item label="下单时间">{{ formatTime(currentOrder.create_time) }}</el-descriptions-item>
          <el-descriptions-item label="商品名称">{{ currentOrder.product_name }}</el-descriptions-item>
          <el-descriptions-item label="购买数量">{{ currentOrder.quantity }}</el-descriptions-item>
          <el-descriptions-item label="单价">¥{{ currentOrder.unit_price }}</el-descriptions-item>
          <el-descriptions-item label="总金额">¥{{ currentOrder.total_amount }}</el-descriptions-item>
          <el-descriptions-item label="实付金额">
            <span class="pay-amount">¥{{ currentOrder.pay_amount }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="支付状态">
            <el-tag :type="currentOrder.pay_status === 1 ? 'success' : 'info'" size="small">
              {{ currentOrder.pay_status === 1 ? '已支付' : '未支付' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="订单状态">{{ getOrderStatusText(currentOrder.order_status) }}</el-descriptions-item>
          <el-descriptions-item label="退款状态">{{ getRefundStatusText(currentOrder.refund_status)
          }}</el-descriptions-item>
          <el-descriptions-item label="核销码">{{ currentOrder.verification_code || '--' }}</el-descriptions-item>
          <el-descriptions-item v-if="currentOrder.verification_time" label="核销时间">{{
            formatTime(currentOrder.verification_time) }}</el-descriptions-item>
          <el-descriptions-item v-if="currentOrder.verification_staff_name" label="核销员">{{
            currentOrder.verification_staff_name }}</el-descriptions-item>
          <el-descriptions-item label="用户昵称">{{ currentOrder.user_nickname || '--' }}</el-descriptions-item>
          <el-descriptions-item label="用户电话">{{ currentOrder.user_phone || '--' }}</el-descriptions-item>

          <!-- 退款详情 - 只有当有退款信息时才显示 -->
          <template v-if="currentOrder.refund_status > 0">
            <el-descriptions-item label="退款金额" v-if="currentOrder.refund_amount">
              ¥{{ currentOrder.refund_amount }}
            </el-descriptions-item>
            <el-descriptions-item label="退款时间" v-if="currentOrder.refund_time">
              {{ formatTime(currentOrder.refund_time) }}
            </el-descriptions-item>
            <el-descriptions-item label="退款原因" v-if="currentOrder.refund_reason" :span="2">
              {{ currentOrder.refund_reason }}
            </el-descriptions-item>
            <el-descriptions-item label="退款备注" v-if="currentOrder.refund_remark" :span="2">
              {{ currentOrder.refund_remark }}
            </el-descriptions-item>
            <el-descriptions-item label="审核时间" v-if="currentOrder.refund_audit_time">
              {{ formatTime(currentOrder.refund_audit_time) }}
            </el-descriptions-item>
            <el-descriptions-item label="凭证图片" v-if="currentOrder.refund_images && currentOrder.refund_images.length"
              :span="2">
              <div class="refund-images">
                <el-image v-for="(img, idx) in currentOrder.refund_images" :key="idx" :src="img"
                  :preview-src-list="currentOrder.refund_images" fit="cover" class="refund-img" :initial-index="idx" />
              </div>
            </el-descriptions-item>
          </template>
        </el-descriptions>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
import { CopyDocument, Plus } from '@element-plus/icons-vue'

export default {
  name: 'OrderManagementPage',
  components: { CopyDocument, Plus },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const agentId = ref(userInfo.agent_id || 0)
    const storeId = ref(userInfo.id)

    const loading = ref(false)
    const searchKey = ref('')
    const filterOrderStatus = ref(null)
    const filterRefundStatus = ref(null)
    const storeFilter = ref(null)
    const branchFilter = ref(null)
    const dateRange = ref([])
    const orderList = ref([])
    const storeList = ref([])
    const branchList = ref([])
    const staffList = ref([])
    const stats = ref({})

    const verificationDialogVisible = ref(false)
    const verificationLoading = ref(false)
    const detailDialogVisible = ref(false)
    const refundApplyDialogVisible = ref(false)
    const refundApplyLoading = ref(false)
    const refundAuditDialogVisible = ref(false)
    const refundAuditLoading = ref(false)
    const currentOrder = ref(null)

    const verificationForm = reactive({
      staff_id: '',
      staff_name: '',
      store_name: '',
      branch_name: '',
      remark: ''
    })

    const refundApplyForm = reactive({
      refund_reason: '',
      refund_remark: ''
    })
    const refundApplyImages = ref([])

    const refundAuditForm = reactive({
      audit_result: 'approved',
      refund_type: 'online',
      refund_remark: ''
    })
    const refundAuditImages = ref([])

    const pagination = reactive({
      page: 1,
      limit: 10,
      total: 0
    })

    const getOrderStatusText = (status) => {
      const map = { 0: '待支付', 1: '待使用', 2: '已使用', 3: '已退款', 4: '已取消' }
      return map[status] || '未知'
    }

    const getOrderStatusType = (status) => {
      const map = { 0: 'info', 1: 'warning', 2: 'success', 3: 'danger', 4: 'info' }
      return map[status] || 'info'
    }

    const getRefundStatusText = (status) => {
      const map = { 0: '无退款', 1: '退款中', 2: '退款成功', 3: '退款失败' }
      return map[status] || '未知'
    }

    const getRefundStatusType = (status) => {
      const map = { 0: 'info', 1: 'warning', 2: 'success', 3: 'danger' }
      return map[status] || 'info'
    }

    const formatTime = (time) => {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    }

    const copyCode = async (code) => {
      try {
        await navigator.clipboard.writeText(code)
        ElMessage.success('核销码已复制')
      } catch {
        ElMessage.error('复制失败')
      }
    }

    // 参考 AddProduct.vue 中的 uploadImage 函数
    const uploadImage = async (file) => {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        ElMessage.error('只能上传图片文件')
        return null
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        ElMessage.error('图片大小不能超过 5MB')
        return null
      }
      try {
        const signature = await getUploadSignature('image')
        const ossRes = await uploadToOSS(file, signature)
        return ossRes.url
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error(error.message || '上传失败')
        return null
      }
    }

    // 申请退款 - 图片上传（参考 AddProduct.vue 的 handleImagesChange）
    const handleRefundImageChange = async (file, fileList) => {
      if (!file.raw) return
      const loadingInstance = ElLoading.service({ text: '上传中...' })
      try {
        const url = await uploadImage(file.raw)
        if (url) {
          // 参考 AddProduct.vue：直接使用 fileList 并更新 url
          // 将当前上传的图片 url 设置到对应的文件项中
          const newFileList = fileList.map(f => {
            if (f.uid === file.uid) {
              return { uid: f.uid, name: f.name, url: url, status: 'success' }
            }
            return f
          })
          refundApplyImages.value = newFileList
          ElMessage.success('上传成功')
        }
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error(error.message || '上传失败')
        // 移除失败的文件
        refundApplyImages.value = fileList.filter(f => f.uid !== file.uid)
      } finally {
        loadingInstance.close()
      }
    }

    const handleRefundImageRemove = (file) => {
      refundApplyImages.value = refundApplyImages.value.filter(f => f.uid !== file.uid)
    }

    // 审核退款 - 图片上传
    const handleAuditImageChange = async (file, fileList) => {
      if (!file.raw) return
      const loadingInstance = ElLoading.service({ text: '上传中...' })
      try {
        const url = await uploadImage(file.raw)
        if (url) {
          const newFileList = fileList.map(f => {
            if (f.uid === file.uid) {
              return { uid: f.uid, name: f.name, url: url, status: 'success' }
            }
            return f
          })
          refundAuditImages.value = newFileList
          ElMessage.success('上传成功')
        }
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error(error.message || '上传失败')
        refundAuditImages.value = fileList.filter(f => f.uid !== file.uid)
      } finally {
        loadingInstance.close()
      }
    }

    const handleAuditImageRemove = (file) => {
      refundAuditImages.value = refundAuditImages.value.filter(f => f.uid !== file.uid)
    }


    const fetchStores = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          storeList.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取门店失败:', error)
      }
    }

    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/store_branch_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, limit: 100 }
        })
        if (response.data.code === 200) {
          branchList.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取分店失败:', error)
      }
    }

    const fetchStaffList = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/staff_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, role: 'clerk', status: 1, limit: 100 }
        })
        if (response.data.code === 200) {
          staffList.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取核销员列表失败:', error)
      }
    }

    const onStaffChange = (staffId) => {
      const staff = staffList.value.find(s => s.id === staffId)
      if (staff) {
        verificationForm.staff_name = staff.name
        const store = storeList.value.find(s => s.id === staff.branch_id)
        verificationForm.store_name = store ? store.store_name : ''
        const branch = branchList.value.find(b => b.id === staff.store_branch_id)
        verificationForm.branch_name = branch ? branch.branch_name : ''
      }
    }

    const fetchData = async () => {
      loading.value = true
      try {
        const params = {
          agent_id: agentId.value,
          store_id: storeId.value,
          page: pagination.page,
          limit: pagination.limit,
          search: searchKey.value,
          order_status: filterOrderStatus.value,
          refund_status: filterRefundStatus.value
        }
        if (storeFilter.value) {
          params.branch_id = storeFilter.value
        }
        if (branchFilter.value) {
          params.store_branch_id = branchFilter.value
        }
        // 修复：日期参数需要正确传递
        if (dateRange.value && dateRange.value.length === 2) {
          params.start_date = dateRange.value[0]
          params.end_date = dateRange.value[1]
        }

        const response = await axios.get(`${config.BASE_URL}/shop/api/order_api.php`, { params })

        if (response.data.code === 200) {
          orderList.value = response.data.data.list
          stats.value = response.data.data.stats
          pagination.total = response.data.data.pagination.total
        }
      } catch (error) {
        console.error('获取订单列表失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      pagination.page = 1
      fetchData()
    }

    const resetSearch = () => {
      searchKey.value = ''
      filterOrderStatus.value = null
      filterRefundStatus.value = null
      storeFilter.value = null
      branchFilter.value = null
      dateRange.value = []
      pagination.page = 1
      fetchData()
    }

    const handleVerification = (row) => {
      currentOrder.value = row
      verificationForm.staff_id = ''
      verificationForm.staff_name = ''
      verificationForm.store_name = ''
      verificationForm.branch_name = ''
      verificationForm.remark = ''
      verificationDialogVisible.value = true
    }

    const confirmVerification = async () => {
      if (!verificationForm.staff_id) {
        ElMessage.warning('请选择核销员')
        return
      }

      verificationLoading.value = true
      try {
        const staff = staffList.value.find(s => s.id === verificationForm.staff_id)
        const response = await axios.put(`${config.BASE_URL}/shop/api/order_api.php`, {
          id: currentOrder.value.id,
          agent_id: agentId.value,
          store_id: storeId.value,
          order_status: 2,
          staff_id: verificationForm.staff_id,
          staff_name: verificationForm.staff_name,
          branch_id: staff?.branch_id,
          remark: verificationForm.remark
        })

        if (response.data.code === 200) {
          ElMessage.success('核销成功')
          verificationDialogVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.data.message || '核销失败')
        }
      } catch (error) {
        console.error('核销失败:', error)
        ElMessage.error('核销失败，请稍后重试')
      } finally {
        verificationLoading.value = false
      }
    }

    const handleCancel = async (row) => {
      try {
        await ElMessageBox.confirm('确定要取消该订单吗？', '提示', { type: 'warning' })

        const response = await axios.put(`${config.BASE_URL}/shop/api/order_api.php`, {
          id: row.id,
          store_id: storeId.value,
          order_status: 4,
          pay_status: 0
        })

        if (response.data.code === 200) {
          ElMessage.success('订单已取消')
          fetchData()
        } else {
          ElMessage.error(response.data.message || '取消失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('取消失败')
        }
      }
    }

    const applyRefund = (row) => {
      currentOrder.value = row
      refundApplyForm.refund_reason = ''
      refundApplyForm.refund_remark = ''
      refundApplyImages.value = []
      refundApplyDialogVisible.value = true
    }

    const submitRefundApply = async () => {
      if (!refundApplyForm.refund_reason) {
        ElMessage.warning('请填写退款原因')
        return
      }

      refundApplyLoading.value = true
      try {
        const response = await axios.put(`${config.BASE_URL}/shop/api/order_api.php`, {
          id: currentOrder.value.id,
          store_id: storeId.value,
          action: 'apply_refund',
          refund_reason: refundApplyForm.refund_reason,
          refund_remark: refundApplyForm.refund_remark,
          refund_images: refundApplyImages.value.map(img => img.url)
        })

        if (response.data.code === 200) {
          ElMessage.success('退款申请已提交')
          refundApplyDialogVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.data.message || '提交失败')
        }
      } catch (error) {
        console.error('提交退款申请失败:', error)
        ElMessage.error('提交失败')
      } finally {
        refundApplyLoading.value = false
      }
    }

    const auditRefund = (row) => {
      currentOrder.value = row
      refundAuditForm.audit_result = 'approved'
      refundAuditForm.refund_type = 'online'
      refundAuditForm.refund_remark = ''
      refundAuditImages.value = []
      refundAuditDialogVisible.value = true
    }

    const submitRefundAudit = async () => {
      refundAuditLoading.value = true
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/order_api.php`, {
          id: currentOrder.value.id,
          store_id: storeId.value,
          action: 'refund_audit',
          audit_result: refundAuditForm.audit_result,
          refund_type: refundAuditForm.refund_type,
          refund_remark: refundAuditForm.refund_remark,
          refund_images: refundAuditImages.value.map(img => img.url)
        })

        if (response.data.code === 200) {
          ElMessage.success(response.data.message)
          refundAuditDialogVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.data.message || '审核失败')
        }
      } catch (error) {
        console.error('审核退款失败:', error)
        ElMessage.error('审核失败')
      } finally {
        refundAuditLoading.value = false
      }
    }

    const viewDetail = (row) => {
      currentOrder.value = row
      detailDialogVisible.value = true
    }

    onMounted(() => {
      fetchStores()
      fetchBranches()
      fetchStaffList()
      fetchData()
    })

    return {
      loading,
      searchKey,
      filterOrderStatus,
      filterRefundStatus,
      storeFilter,
      branchFilter,
      dateRange,
      orderList,
      storeList,
      branchList,
      staffList,
      stats,
      pagination,
      verificationDialogVisible,
      verificationLoading,
      detailDialogVisible,
      refundApplyDialogVisible,
      refundApplyLoading,
      refundAuditDialogVisible,
      refundAuditLoading,
      currentOrder,
      verificationForm,
      refundApplyForm,
      refundApplyImages,
      refundAuditForm,
      refundAuditImages,
      getOrderStatusText,
      getOrderStatusType,
      getRefundStatusText,
      getRefundStatusType,
      formatTime,
      copyCode,
      onStaffChange,
      handleRefundImageChange,
      handleRefundImageRemove,
      handleAuditImageChange,
      handleAuditImageRemove,
      handleSearch,
      resetSearch,
      handleVerification,
      confirmVerification,
      handleCancel,
      applyRefund,
      submitRefundApply,
      auditRefund,
      submitRefundAudit,
      viewDetail,
      fetchData
    }
  }
}
</script>

<style scoped lang="scss">
.order-management {
  .page-header {
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }


  .stat-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;

    .stat-card {
      flex: 1;
      min-width: 100px;
      background: #fff;
      border-radius: 12px;
      padding: 16px 20px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      &.warning .stat-value {
        color: #e6a23c;
      }

      &.primary .stat-value {
        color: #409eff;
      }

      &.success .stat-value {
        color: #67c23a;
      }

      &.danger .stat-value {
        color: #f56c6c;
      }

      &.info .stat-value {
        color: #909399;
      }

      .stat-value {
        font-size: 28px;
        font-weight: 700;
      }

      .stat-label {
        font-size: 13px;
        color: #8c8c8c;
        margin-top: 4px;
      }
    }
  }

  .filter-bar {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 20px;
    align-items: center;
  }

  .order-info {
    .order-no {
      font-size: 12px;
      color: #8c8c8c;
      margin-bottom: 4px;
    }

    .product-name {
      font-weight: 500;
      margin-bottom: 4px;
    }

    .user-info {
      font-size: 12px;
      color: #8c8c8c;
    }
  }

  .amount-info {
    .pay-amount {
      font-size: 16px;
      font-weight: 600;
      color: #f56c6c;
    }

    .quantity {
      font-size: 12px;
      color: #666;
    }
  }

  .verification-code {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    .code {
      font-family: monospace;
      font-size: 14px;
      font-weight: 500;
    }
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }

  .action-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .verification-content {
    .order-info {
      background: #f5f5f5;
      padding: 12px;
      border-radius: 8px;
      margin-bottom: 16px;

      .code {
        font-size: 18px;
        color: #409eff;
      }
    }
  }

  .order-detail {
    .pay-amount {
      color: #f56c6c;
      font-weight: 600;
    }
  }

  .refund-images {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    .refund-img {
      width: 80px;
      height: 80px;
      object-fit: cover;
      border-radius: 8px;
      cursor: pointer;
    }
  }

  .refund-amount,
  .refund-reason,
  .refund-remark {
    color: #666;
  }

  .image-uploader {
    width: 100%;

    .upload-tip {
      font-size: 12px;
      color: #8c8c8c;
      margin-top: 8px;
    }
  }
}
</style>